import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AdminHeader from "@/components/admin/AdminHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Info,
  Globe,
  FileText,
} from "lucide-react";

interface SEOIssue {
  id: string;
  source: "page" | "content_item";
  entityId: string;
  title: string;
  slug: string;
  issue: string;
  severity: "error" | "warning" | "info";
}

const SEOHygieneDashboard = () => {
  const { data: issues, isLoading } = useQuery({
    queryKey: ["seo-hygiene"],
    queryFn: async () => {
      // Fetch both tables in parallel
      const [pagesRes, itemsRes] = await Promise.all([
        supabase.from("page_content").select("id, page_title, slug, content, status") as any,
        supabase.from("content_items").select("id, title, slug, seo, status, type") as any,
      ]);

      if (pagesRes.error) throw pagesRes.error;
      if (itemsRes.error) throw itemsRes.error;

      const allIssues: SEOIssue[] = [];
      const allSlugs: string[] = [];
      const allTitles: string[] = [];

      // Check pages
      (pagesRes.data || []).forEach((page: any) => {
        if (page.status === "archived") return;
        const seo = (page.content && page.content._seo) || {};
        allSlugs.push(page.slug);

        if (!seo.title || String(seo.title).trim() === "") {
          allIssues.push({
            id: `p-${page.id}-no-title`,
            source: "page",
            entityId: page.id,
            title: page.page_title,
            slug: page.slug,
            issue: "Missing SEO title",
            severity: "error",
          });
        } else {
          allTitles.push(seo.title);
          if (String(seo.title).length > 60) {
            allIssues.push({
              id: `p-${page.id}-title-long`,
              source: "page",
              entityId: page.id,
              title: page.page_title,
              slug: page.slug,
              issue: `SEO title too long (${String(seo.title).length}/60 chars)`,
              severity: "warning",
            });
          }
        }

        if (!seo.description || String(seo.description).trim() === "") {
          allIssues.push({
            id: `p-${page.id}-no-desc`,
            source: "page",
            entityId: page.id,
            title: page.page_title,
            slug: page.slug,
            issue: "Missing meta description",
            severity: "error",
          });
        } else if (String(seo.description).length > 160) {
          allIssues.push({
            id: `p-${page.id}-desc-long`,
            source: "page",
            entityId: page.id,
            title: page.page_title,
            slug: page.slug,
            issue: `Meta description too long (${String(seo.description).length}/160 chars)`,
            severity: "warning",
          });
        } else if (String(seo.description).length < 140) {
          allIssues.push({
            id: `p-${page.id}-desc-short`,
            source: "page",
            entityId: page.id,
            title: page.page_title,
            slug: page.slug,
            issue: `Meta description too short (${String(seo.description).length}/140 chars min)`,
            severity: "warning",
          });
        }

        if (seo.indexable === false) {
          allIssues.push({
            id: `p-${page.id}-noindex`,
            source: "page",
            entityId: page.id,
            title: page.page_title,
            slug: page.slug,
            issue: "Page set to noindex",
            severity: "info",
          });
        }

        if (!seo.canonicalUrl) {
          allIssues.push({
            id: `p-${page.id}-no-canonical`,
            source: "page",
            entityId: page.id,
            title: page.page_title,
            slug: page.slug,
            issue: "No canonical URL set",
            severity: "info",
          });
        }
      });

      // Check content items
      (itemsRes.data || []).forEach((item: any) => {
        if (item.status === "archived") return;
        const seo = item.seo || {};
        allSlugs.push(item.slug);

        if (!seo.title || String(seo.title).trim() === "") {
          allIssues.push({
            id: `i-${item.id}-no-title`,
            source: "content_item",
            entityId: item.id,
            title: item.title,
            slug: item.slug,
            issue: "Missing SEO title",
            severity: "error",
          });
        } else {
          allTitles.push(seo.title);
          if (String(seo.title).length > 60) {
            allIssues.push({
              id: `i-${item.id}-title-long`,
              source: "content_item",
              entityId: item.id,
              title: item.title,
              slug: item.slug,
              issue: `SEO title too long (${String(seo.title).length}/60 chars)`,
              severity: "warning",
            });
          }
        }

        if (!seo.description || String(seo.description).trim() === "") {
          allIssues.push({
            id: `i-${item.id}-no-desc`,
            source: "content_item",
            entityId: item.id,
            title: item.title,
            slug: item.slug,
            issue: "Missing meta description",
            severity: "error",
          });
        } else if (String(seo.description).length > 160) {
          allIssues.push({
            id: `i-${item.id}-desc-long`,
            source: "content_item",
            entityId: item.id,
            title: item.title,
            slug: item.slug,
            issue: `Meta description too long (${String(seo.description).length}/160 chars)`,
            severity: "warning",
          });
        }
      });

      // Check duplicate slugs
      const slugCounts: Record<string, number> = {};
      allSlugs.forEach((s) => { slugCounts[s] = (slugCounts[s] || 0) + 1; });
      Object.entries(slugCounts).forEach(([slug, count]) => {
        if (count > 1) {
          allIssues.push({
            id: `dup-slug-${slug}`,
            source: "page",
            entityId: "",
            title: `Slug: ${slug}`,
            slug,
            issue: `Duplicate slug used ${count} times`,
            severity: "error",
          });
        }
      });

      // Check duplicate SEO titles
      const titleCounts: Record<string, number> = {};
      allTitles.forEach((t) => { titleCounts[t] = (titleCounts[t] || 0) + 1; });
      Object.entries(titleCounts).forEach(([title, count]) => {
        if (count > 1) {
          allIssues.push({
            id: `dup-title-${title.slice(0, 20)}`,
            source: "page",
            entityId: "",
            title: `Title: ${title}`,
            slug: "",
            issue: `Duplicate SEO title used ${count} times`,
            severity: "warning",
          });
        }
      });

      return allIssues;
    },
  });

  const errors = issues?.filter((i) => i.severity === "error") || [];
  const warnings = issues?.filter((i) => i.severity === "warning") || [];
  const infos = issues?.filter((i) => i.severity === "info") || [];

  const severityIcon = (s: string) => {
    if (s === "error") return <AlertCircle className="h-4 w-4 text-destructive" />;
    if (s === "warning") return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    return <Info className="h-4 w-4 text-blue-500" />;
  };

  const linkTo = (issue: SEOIssue) => {
    if (issue.source === "page") return "/admin/content";
    return "/admin/content-items";
  };

  return (
    <div className="min-h-screen">
      <AdminHeader
        title="SEO Hygiene Dashboard"
        description="Live-computed SEO issues across all pages and content"
      />

      <main className="p-6 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4 max-w-2xl">
          <Card>
            <CardContent className="py-4 text-center">
              <AlertCircle className="h-6 w-6 text-destructive mx-auto mb-2" />
              <div className="text-2xl font-bold">{isLoading ? "—" : errors.length}</div>
              <p className="text-xs text-muted-foreground">Errors</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <AlertTriangle className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
              <div className="text-2xl font-bold">{isLoading ? "—" : warnings.length}</div>
              <p className="text-xs text-muted-foreground">Warnings</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <Info className="h-6 w-6 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold">{isLoading ? "—" : infos.length}</div>
              <p className="text-xs text-muted-foreground">Info</p>
            </CardContent>
          </Card>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        ) : !issues || issues.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-3" />
              <p className="text-lg font-medium">All Clear</p>
              <p className="text-sm text-muted-foreground">No SEO issues detected across your site</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Errors first */}
            {errors.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-destructive mb-3">
                  Errors ({errors.length})
                </h3>
                <Card>
                  <CardContent className="py-0 divide-y divide-border">
                    {errors.map((issue) => (
                      <div key={issue.id} className="flex items-center gap-3 py-3 px-2">
                        {severityIcon(issue.severity)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{issue.title}</p>
                          <p className="text-xs text-muted-foreground">{issue.issue}</p>
                        </div>
                        {issue.slug && (
                          <span className="text-xs text-muted-foreground">/{issue.slug}</span>
                        )}
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={linkTo(issue)}>Fix</Link>
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-yellow-600 mb-3">
                  Warnings ({warnings.length})
                </h3>
                <Card>
                  <CardContent className="py-0 divide-y divide-border">
                    {warnings.map((issue) => (
                      <div key={issue.id} className="flex items-center gap-3 py-3 px-2">
                        {severityIcon(issue.severity)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{issue.title}</p>
                          <p className="text-xs text-muted-foreground">{issue.issue}</p>
                        </div>
                        {issue.slug && (
                          <span className="text-xs text-muted-foreground">/{issue.slug}</span>
                        )}
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={linkTo(issue)}>Fix</Link>
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Info */}
            {infos.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-blue-600 mb-3">
                  Info ({infos.length})
                </h3>
                <Card>
                  <CardContent className="py-0 divide-y divide-border">
                    {infos.map((issue) => (
                      <div key={issue.id} className="flex items-center gap-3 py-3 px-2">
                        {severityIcon(issue.severity)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{issue.title}</p>
                          <p className="text-xs text-muted-foreground">{issue.issue}</p>
                        </div>
                        {issue.slug && (
                          <span className="text-xs text-muted-foreground">/{issue.slug}</span>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default SEOHygieneDashboard;
